<template>
    <span :class="avatarClass" :style="sizeStyle">
        <img
            v-if="(src || srcSet) && !hasLoadError"
            :src="src"
            :alt="alt"
            :srcset="srcSet"
            :style="{ objectFit: fit }"
            @error="handleError"
        />
        <pt-icon v-else-if="icon" :icon="icon"></pt-icon>
        <slot v-else></slot>
    </span>
</template>
<script lang="ts">
export default {
    name: 'PtAvatar',
};
</script>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

interface Props {
    size?: 'large' | 'medium' | 'small' | number;
    shape?: 'circle' | 'square';
    icon?: string;
    src?: string;
    alt?: string;
    srcSet?: string;
    fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

const props = withDefaults(defineProps<Props>(), {
    size: 'large',
    shape: 'circle',
    fit: 'cover',
});

const emit = defineEmits(['error']);

const hasLoadError = ref(false);

watch(
    () => props.src,
    () => {
        hasLoadError.value = false;
    },
);

const avatarClass = computed(() => {
    const { size, icon, shape } = props;
    const classList = ['pt-avatar'];
    if (size && typeof size === 'string') {
        classList.push(`pt-avatar--${size}`);
    }
    if (icon) {
        classList.push('pt-avatar--icon');
    }
    if (shape) {
        classList.push(`pt-avatar--${shape}`);
    }
    return classList;
});

const sizeStyle = computed(() => {
    const { size } = props;
    return typeof size === 'number'
        ? {
              height: `${size}px`,
              width: `${size}px`,
              lineHeight: `${size}px`,
          }
        : {};
});

function handleError(e: Event) {
    hasLoadError.value = true;
    emit('error', e);
}
</script>

<style lang="scss" src="../../theme-chalk/src/avatar.scss" />
